/**
 * Returns IDs present in `oldList` but missing from `newList`.
 */
export function findDisassociatedIds(
    oldList: string[],
    newList: string[]
): string[] {
    return oldList.filter(
        (id) => id !== undefined && newList.indexOf(id) === -1
    );
}

/**
 * Builds { [id]: { [idKey]: id, designation } } for each disassociated ID.
 */
export function buildDisassociateMap(
    ids: string[],
    subRows: any[],
    idKey: string,
    deviceLabel: string
): Record<string, any> {
    const map: Record<string, any> = {};
    ids.forEach((id) => {
        let name = "";
        subRows.forEach((el: any) => {
            if (el.peripheralId === id) name = el[deviceLabel];
        });
        map[id] = { [idKey]: id, designation: name };
    });
    return map;
}

/**
 * Maps `cash_management` → `cashManagement`, keeps others as-is.
 */
export function getDeviceTypeForApi(peripheralType: string): string {
    return peripheralType === "cash_management"
        ? "cashManagement"
        : peripheralType;
}

/**
 * Checks if multiple shops are selected (for disabling add-application in multi-shop).
 */
export function hasMultipleShops(
    selectedRows: Record<string, any>[],
    shopLabel: string
): boolean {
    if (!selectedRows || selectedRows.length === 0) return true;
    const shopsWithName = Object.values(selectedRows).filter(
        (row: any) => row[shopLabel] !== ""
    );
    return shopsWithName.length !== 1;
}

/**
 * Returns true when no shop row is selected. A shop row is identified by
 * having a non-empty shop-label cell (i.e. `row[shopLabel] !== ""`).
 *
 * Used to gate the multi-shop "Add an application" entry point: the button
 * must be enabled as soon as the user has selected at least one shop —
 * unlike `hasMultipleShops` which only allowed exactly one.
 */
export function hasNoShopSelected(
    selectedRows: Record<string, any>[],
    shopLabel: string
): boolean {
    if (!selectedRows || selectedRows.length === 0) return true;
    const shopsWithName = Object.values(selectedRows).filter(
        (row: any) => row[shopLabel] !== ""
    );
    return shopsWithName.length === 0;
}

/**
 * Collects the non-empty `shopId` values from the selected rows into a
 * Set. Used by the multi-shop validation memos in AddApplication
 * (designation/IP/codeCaisse uniqueness) so a conflict in any of the
 * target shops is flagged inline before the fan-out POST.
 *
 * `selectedRows` can be:
 *  - empty (form opened from an empty selection) → empty Set
 *  - shop rows being targeted for add → one shopId per row
 *  - a single app row being edited → that app's shopId
 */
export function buildTargetShopIdsSet(
    selectedRows: Record<string, any>[] | null | undefined
): Set<string> {
    if (!selectedRows || selectedRows.length === 0) return new Set();
    return new Set(
        selectedRows
            .map((row: any) => row?.shopId)
            .filter(
                (id: unknown): id is string =>
                    typeof id === "string" && id.length > 0
            )
    );
}

/**
 * Flattens a selection that may contain peripheral rows, application rows, or
 * shop rows into the list of peripheral leaves (nodes carrying
 * `peripheralType`), descending into `subRows` for app/shop rows.
 */
export function collectPeripheralRows(rows: any[]): any[] {
    const collected: any[] = [];
    const walk = (node: any) => {
        if (!node) return;
        if (node.peripheralType !== undefined) {
            collected.push(node);
            return;
        }
        if (Array.isArray(node.subRows)) node.subRows.forEach(walk);
    };
    (rows ?? []).forEach(walk);
    return collected;
}

export type AppGroup = {
    appId: string;
    designation: string;
    shopId: string;
    items: any[];
};

/**
 * Groups peripheral rows by their owning application (`appId`), carrying the
 * application designation and shopId. Rows without an `appId` are skipped.
 */
export function groupPeripheralsByApplication(peripherals: any[]): AppGroup[] {
    const groups = new Map<string, AppGroup>();
    peripherals.forEach((peripheral) => {
        const appId = peripheral.appId;
        if (!appId) return;
        if (!groups.has(appId)) {
            groups.set(appId, {
                appId,
                designation: peripheral.applicationDesignation,
                shopId: peripheral.shopId,
                items: [],
            });
        }
        groups.get(appId)!.items.push(peripheral);
    });
    return Array.from(groups.values());
}

/**
 * Checks if any selected row has associated peripherals.
 */
export function hasAssociatedPeripherals(
    selectedRows: Record<string, any>[],
    shopLabel: string
): boolean {
    if (!selectedRows || selectedRows.length === 0) return false;
    return Object.values(selectedRows).some(
        (row: any) =>
            row[shopLabel] === "" &&
            row.subRows !== undefined &&
            row.subRows.length > 0
    );
}
